import { useState } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { GripVertical, ChevronDown, ChevronRight, Plus, X, Trash2, Settings2 } from 'lucide-react';
import { TYPE_COLORS, type FieldMapping, type FieldLibraryItem } from '@/lib/admin/field-utils';
import { FieldPicker } from './FieldPicker';

interface FieldBuilderProps {
  fields: FieldMapping[];
  onChange: (fields: FieldMapping[]) => void;
  lookupGroups: string[];
}

export function FieldBuilder({ fields, onChange, lookupGroups }: FieldBuilderProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set());

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(fields);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update display_order
    const updatedItems = items.map((item, index) => ({
      ...item,
      display_order: index,
    }));

    onChange(updatedItems);
  };

  const handleAddFields = (newFields: FieldLibraryItem[]) => {
    const startOrder = fields.length;
    const mappings: FieldMapping[] = newFields.map((field, index) => ({
      field_id: field.id,
      field,
      is_required: false,
      default_value: null,
      lookup_group: null,
      inline_enum: null,
      display_order: startOrder + index,
      notes: null,
    }));

    onChange([...fields, ...mappings]);
  };

  const handleRemoveField = (index: number) => {
    const newFields = fields.filter((_, i) => i !== index);
    onChange(newFields.map((f, i) => ({ ...f, display_order: i })));
  };

  const handleUpdateField = (index: number, updates: Partial<FieldMapping>) => {
    const newFields = [...fields];
    newFields[index] = { ...newFields[index], ...updates };
    onChange(newFields);
  };

  const toggleExpanded = (fieldId: string) => {
    const newExpanded = new Set(expandedFields);
    if (newExpanded.has(fieldId)) {
      newExpanded.delete(fieldId);
    } else {
      newExpanded.add(fieldId);
    }
    setExpandedFields(newExpanded);
  };

  const handleAddEnumValue = (index: number) => {
    const field = fields[index];
    const currentEnum = field.inline_enum || [];
    handleUpdateField(index, { inline_enum: [...currentEnum, ''] });
  };

  const handleUpdateEnumValue = (fieldIndex: number, enumIndex: number, value: string) => {
    const field = fields[fieldIndex];
    const newEnum = [...(field.inline_enum || [])];
    newEnum[enumIndex] = value;
    handleUpdateField(fieldIndex, { inline_enum: newEnum });
  };

  const handleRemoveEnumValue = (fieldIndex: number, enumIndex: number) => {
    const field = fields[fieldIndex];
    const newEnum = (field.inline_enum || []).filter((_, i) => i !== enumIndex);
    handleUpdateField(fieldIndex, { inline_enum: newEnum.length > 0 ? newEnum : null });
  };

  const excludeFieldIds = fields.map((f) => f.field_id);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h4 className="text-sm font-medium">Fields ({fields.length})</h4>
          <p className="text-xs text-muted-foreground">Drag to reorder, click to expand settings</p>
        </div>
        <Button onClick={() => setPickerOpen(true)} size="sm">
          <Plus className="h-4 w-4 mr-1" />
          Add Fields
        </Button>
      </div>

      {fields.length === 0 ? (
        <div className="border-2 border-dashed rounded-lg p-8 text-center">
          <Settings2 className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
          <p className="text-muted-foreground">No fields added yet</p>
          <Button variant="outline" className="mt-3" onClick={() => setPickerOpen(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Add Fields from Library
          </Button>
        </div>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="fields">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-2">
                {fields.map((mapping, index) => {
                  const isExpanded = expandedFields.has(mapping.field_id);
                  const field = mapping.field;

                  return (
                    <Draggable key={mapping.field_id} draggableId={mapping.field_id} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`border rounded-lg bg-background ${
                            snapshot.isDragging ? 'shadow-lg ring-2 ring-primary' : ''
                          }`}
                        >
                          <Collapsible open={isExpanded} onOpenChange={() => toggleExpanded(mapping.field_id)}>
                            {/* Header Row */}
                            <div className="flex items-center gap-2 p-3">
                              <div {...provided.dragHandleProps} className="cursor-grab text-muted-foreground">
                                <GripVertical className="h-4 w-4" />
                              </div>

                              <CollapsibleTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-6 w-6">
                                  {isExpanded ? (
                                    <ChevronDown className="h-4 w-4" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4" />
                                  )}
                                </Button>
                              </CollapsibleTrigger>

                              <div className="flex-1 min-w-0 flex items-center gap-2">
                                <span className="font-mono text-sm font-medium truncate">{field.slug}</span>
                                <Badge className={TYPE_COLORS[field.data_type] || ''} variant="secondary">
                                  {field.data_type}
                                </Badge>
                                {field.format && (
                                  <Badge variant="outline" className="text-xs">
                                    {field.format}
                                  </Badge>
                                )}
                                {mapping.inline_enum && mapping.inline_enum.length > 0 && (
                                  <Badge variant="secondary" className="text-xs">
                                    {mapping.inline_enum.length} values
                                  </Badge>
                                )}
                              </div>

                              <div className="flex items-center gap-3">
                                <div className="flex items-center gap-2">
                                  <Switch
                                    checked={mapping.is_required}
                                    onCheckedChange={(checked) =>
                                      handleUpdateField(index, { is_required: checked })
                                    }
                                  />
                                  <Label className="text-xs text-muted-foreground">Required</Label>
                                </div>

                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-destructive hover:text-destructive"
                                  onClick={() => handleRemoveField(index)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>

                            {/* Expanded Settings */}
                            <CollapsibleContent>
                              <div className="px-3 pb-3 pt-0 border-t bg-muted/30">
                                <div className="grid gap-4 pt-3">
                                  {/* Field Info */}
                                  {field.description && (
                                    <div>
                                      <Label className="text-xs text-muted-foreground">Description</Label>
                                      <p className="text-sm">{field.description}</p>
                                    </div>
                                  )}

                                  <div className="grid grid-cols-2 gap-4">
                                    {/* Default Value */}
                                    <div className="space-y-2">
                                      <Label htmlFor={`default-${index}`}>Default Value</Label>
                                      <Input
                                        id={`default-${index}`}
                                        value={mapping.default_value || ''}
                                        onChange={(e) =>
                                          handleUpdateField(index, {
                                            default_value: e.target.value || null,
                                          })
                                        }
                                        placeholder="Optional default value"
                                      />
                                    </div>

                                    {/* Lookup Group */}
                                    <div className="space-y-2">
                                      <Label>Lookup Group</Label>
                                      <Select
                                        value={mapping.lookup_group || 'none'}
                                        onValueChange={(value) =>
                                          handleUpdateField(index, {
                                            lookup_group: value === 'none' ? null : value,
                                          })
                                        }
                                      >
                                        <SelectTrigger>
                                          <SelectValue placeholder="Select lookup..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="none">None</SelectItem>
                                          {lookupGroups.map((group) => (
                                            <SelectItem key={group} value={group}>
                                              {group.replace(/_/g, ' ')}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  </div>

                                  {/* Inline Enum */}
                                  <div className="space-y-2">
                                    <div className="flex justify-between items-center">
                                      <Label>Inline Enum Values</Label>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleAddEnumValue(index)}
                                      >
                                        <Plus className="h-3 w-3 mr-1" />
                                        Add Value
                                      </Button>
                                    </div>
                                    {mapping.inline_enum && mapping.inline_enum.length > 0 ? (
                                      <div className="flex flex-wrap gap-2">
                                        {mapping.inline_enum.map((value, enumIndex) => (
                                          <div key={enumIndex} className="flex items-center gap-1">
                                            <Input
                                              value={value}
                                              onChange={(e) =>
                                                handleUpdateEnumValue(index, enumIndex, e.target.value)
                                              }
                                              className="w-32 h-8"
                                              placeholder="Value"
                                            />
                                            <Button
                                              variant="ghost"
                                              size="icon"
                                              className="h-8 w-8"
                                              onClick={() => handleRemoveEnumValue(index, enumIndex)}
                                            >
                                              <X className="h-3 w-3" />
                                            </Button>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <p className="text-xs text-muted-foreground">
                                        No inline enum values. Use lookup groups or add values here.
                                      </p>
                                    )}
                                  </div>

                                  {/* Notes */}
                                  <div className="space-y-2">
                                    <Label htmlFor={`notes-${index}`}>Notes</Label>
                                    <Textarea
                                      id={`notes-${index}`}
                                      value={mapping.notes || ''}
                                      onChange={(e) =>
                                        handleUpdateField(index, { notes: e.target.value || null })
                                      }
                                      placeholder="Internal notes about this field..."
                                      rows={2}
                                    />
                                  </div>
                                </div>
                              </div>
                            </CollapsibleContent>
                          </Collapsible>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      <FieldPicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onSelect={handleAddFields}
        excludeFieldIds={excludeFieldIds}
      />
    </div>
  );
}
